import React, { useState, useCallback } from "react";
import styled, { createGlobalStyle } from "styled-components";
import _ from "lodash";

import { PresentationLibraryFilterType } from "js/controllers/PresentationLibraryPaneController";
import { WorkspaceResourceFolderType } from "common/interfaces";
import { FolderPathInfo, getFolderPathsForPresentation } from "js/editor/PresentationLibrary/utils/folderPaths";
import { IWorkspaceResource } from "common/interfaces";
import { IWorkspaceResourceFolder } from "common/interfaces";
import { Icon } from "js/Components/Icon";
import { LibraryBreadcrumb } from "js/editor/PresentationLibrary/components/LibraryBreadcrumb";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { Divider } from "js/Components/Divider";
import { Menu, MenuItem } from "js/Components/Menu";

const FOLDER_TOOLTIP_CLASS = "folder-location-tooltip";

const FolderTooltipStyles = createGlobalStyle`
    .${FOLDER_TOOLTIP_CLASS}.MuiTooltip-tooltip {
        padding: 8px 12px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
        max-width: min(600px, 90vw);
        width: max-content;
    }
`;

const FolderTrigger = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    text-transform: capitalize;
    user-select: none;
    min-width: 0;

    &:hover .folder-path-text {
        color: #11A9E2;
        text-decoration: underline;
        text-decoration-color: #11A9E2;
    }
`;

const PathLabelWrapper = styled.span`
    min-width: 0;
    flex: 1;
    display: block;
    overflow: hidden;
`;

const NoFolderPlaceholder = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #999;
    cursor: default;
`;

const SinglePathLabel = styled.span`
    display: block;
    width: 100%;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FolderMenu = styled(Menu)`
    .MuiPaper-root {
        border: none;
        border-radius: 2px;
        box-shadow: 0px 0px 50px rgb(0 0 0 / 25%);
    }

    .MuiList-padding {
        padding-top: 4px;
        padding-bottom: 4px;
        overflow: hidden;
        background: #fff;
    }
`;

const FolderPathItem = styled(MenuItem)`
    &&& {
        min-width: 180px;
        max-width: 320px;
        padding: 8px 12px;
    }

    &&&:hover .folder-path-item-label {
        color: #11A9E2;
        text-decoration: underline;
        text-decoration-color: #11A9E2;
    }
`;

const FolderPathItemContent = styled.div`
    text-transform: capitalize;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #333;
    width: 100%;
    min-width: 0;

    .MuiMenuItem-root:hover & {
        color: #333;
    }
`;

const FolderPathItemLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    min-width: 0;
`;

export interface FolderLocationBreadcrumbProps {
    resource: IWorkspaceResource;
    resourceFolders: IWorkspaceResourceFolder[];
    onNavigateToFolder: (filter: {
        type: PresentationLibraryFilterType.FOLDER | PresentationLibraryFilterType.TEAM_FOLDER;
        folderId: string;
        subFolderId?: string;
    }) => void;
    isGrid?: boolean;
    showDivider?: boolean;
}

function isSharedFolderType(folderType: WorkspaceResourceFolderType) {
    return folderType === WorkspaceResourceFolderType.SHARED ||
        folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
}

function getFolderIconName(folderType?: WorkspaceResourceFolderType) {
    return folderType && isSharedFolderType(folderType) ? "folder_shared" : "folder";
}

export function FolderLocationBreadcrumb({
    showDivider = false,
    resource,
    resourceFolders,
    onNavigateToFolder,
    isGrid = false,
}: FolderLocationBreadcrumbProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const folderPaths = getFolderPathsForPresentation(resource, resourceFolders) ?? [];

    const handlePopoverClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const navigateToFolder = useCallback(({ folderType, parentFolderId, folderId }: FolderPathInfo) => {
        const isTeamFolder = isSharedFolderType(folderType);
        onNavigateToFolder({
            type: isTeamFolder ? PresentationLibraryFilterType.TEAM_FOLDER : PresentationLibraryFilterType.FOLDER,
            folderId: parentFolderId ?? folderId,
            subFolderId: parentFolderId ? folderId : undefined,
        });
    }, [onNavigateToFolder]);

    const handlePathClick = useCallback((event: React.MouseEvent, info: FolderPathInfo) => {
        event.stopPropagation();
        navigateToFolder(info);
        setAnchorEl(null);
    }, [navigateToFolder]);

    const isSingleFolder = folderPaths.length === 1;
    const triggerIconName = isSingleFolder
        ? getFolderIconName(folderPaths[0].folderType)
        : getFolderIconName(folderPaths.every(path => isSharedFolderType(path.folderType)) ? WorkspaceResourceFolderType.SHARED : undefined);
    const open = Boolean(anchorEl);

    if (_.isEmpty(folderPaths)) {
        // Grid view: render nothing to avoid gap
        if (isGrid) {
            return null;
        }

        // Not in any folder: show "--" in list view
        return (
            <NoFolderPlaceholder>
                <span>--</span>
            </NoFolderPlaceholder>
        );
    }

    const pathLabel = isSingleFolder ? (
        <TruncatedTooltip
            title={<LibraryBreadcrumb pathParts={folderPaths[0].pathParts} />}
            tooltipClasses={{ tooltip: FOLDER_TOOLTIP_CLASS }}
        >
            <SinglePathLabel className="folder-path-text">{folderPaths[0].displayLabel}</SinglePathLabel>
        </TruncatedTooltip>
    ) : (
        <span className="folder-path-text">{folderPaths.length} folders</span>
    );

    return (
        <>
            <FolderTooltipStyles />
            {showDivider && isGrid && (
                <Divider
                    vertical
                    margin={8}
                    style={{ background: "rgba(0, 0, 0, 0.1)", borderRadius: "100px", height: 12 }}
                />
            )}
            <FolderTrigger
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                    e.stopPropagation();
                    if (isSingleFolder) {
                        navigateToFolder(folderPaths[0]);
                    } else {
                        setAnchorEl(anchorEl ? null : e.currentTarget);
                    }
                }}
            >
                <Icon size={isGrid ? 16 : 20}>{triggerIconName}</Icon>
                <PathLabelWrapper>{pathLabel}</PathLabelWrapper>
            </FolderTrigger>

            {!isSingleFolder && (
                <FolderMenu
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    onClose={handlePopoverClose}
                    getContentAnchorEl={null}
                    onClick={e => e.stopPropagation()}
                >
                    {folderPaths.map(info => (
                        <FolderPathItem
                            key={info.folderId}
                            onClick={e => handlePathClick(e, info)}
                            onMouseDown={e => e.stopPropagation()}
                        >
                            <FolderPathItemContent>
                                <Icon size={18}>{getFolderIconName(info.folderType)}</Icon>
                                <TruncatedTooltip
                                    title={<LibraryBreadcrumb pathParts={info.pathParts} />}
                                    tooltipClasses={{ tooltip: FOLDER_TOOLTIP_CLASS }}
                                >
                                    <FolderPathItemLabel className="folder-path-item-label">
                                        <LibraryBreadcrumb pathParts={info.pathParts} iconColor="#666" />
                                    </FolderPathItemLabel>
                                </TruncatedTooltip>
                            </FolderPathItemContent>
                        </FolderPathItem>
                    ))}
                </FolderMenu>
            )}
        </>
    );
}
